import { initializeFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { app, emulatorConfig } from './firebaseApp.js';

const FIRESTORE_SETTINGS = Object.freeze({
  experimentalAutoDetectLongPolling: true,
  useFetchStreams: false,
  cache: {
    indexedDbCacheEnabled: true,
  },
  maxOperationRetryTime: 30000,
});

const canonicalDatabaseId = (databaseId) => (databaseId && databaseId !== '(default)' ? databaseId : '(default)');

const registry = new Map([
  ['default', { databaseId: '(default)' }],
  ['legacy', { databaseId: '(default)' }],
  ['whoispaying', { databaseId: '(default)' }],
  ['moments', { databaseId: 'moments-db-og' }],
  ['moments-db-og', { databaseId: 'moments-db-og' }],
]);

const instances = new Map();

function attachEmulator(instance, state) {
  if (!emulatorConfig.useEmulators || state.emulatorAttached) return;
  try {
    connectFirestoreEmulator(instance, emulatorConfig.firestoreHost, emulatorConfig.firestorePort);
    state.emulatorAttached = true;
  } catch (err) {
    console.warn('[FirestoreRegistry] Failed to connect Firestore emulator:', err);
  }
}

function createInstance(databaseId) {
  const canonicalId = canonicalDatabaseId(databaseId);
  const existing = instances.get(canonicalId);
  if (existing) {
    attachEmulator(existing.instance, existing);
    return existing.instance;
  }

  const instance =
    canonicalId === '(default)'
      ? initializeFirestore(app, FIRESTORE_SETTINGS)
      : initializeFirestore(app, FIRESTORE_SETTINGS, canonicalId);

  const state = { instance, emulatorAttached: false };
  instances.set(canonicalId, state);
  attachEmulator(instance, state);
  return instance;
}

function getConfig(name) {
  const key = (name || 'default').toLowerCase();
  return registry.get(key);
}

export function registerDb({ name, databaseId }) {
  if (!name || !databaseId) {
    throw new Error('[FirestoreRegistry] registerDb requires both name and databaseId');
  }
  const key = name.toLowerCase();
  registry.set(key, { databaseId });
  return createInstance(databaseId);
}

export function getDb(name = 'default') {
  const config = getConfig(name);
  if (!config) {
    throw new Error(`[FirestoreRegistry] Unknown database "${name}". Register it with registerDb().`);
  }
  return createInstance(config.databaseId);
}

export function listRegisteredDbs() {
  return Array.from(new Set(Array.from(registry.entries()).map(([, cfg]) => canonicalDatabaseId(cfg.databaseId))));
}
